import {useMemo} from "react";
import {BaseAlert, BaseButton, BaseCard} from "@/components";
import {lexicalToHtml} from "@/util/lexicalToHtml";
import {slugify} from "./news.adapter";
import {useNews} from "./useNews";

const formatDate = (value: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("es-VE", {day: "2-digit", month: "long", year: "numeric"});
};

export interface ArticleDetailPageProps {
  slug: string;
  onBack?: () => void;
}

const ArticleDetailPage = ({slug, onBack}: ArticleDetailPageProps) => {
  const {articles, isLoading, isError, error, refetch} = useNews();

  const article = useMemo(
    () => articles.find(item => item.slug === slug || slugify(item.title) === slug) ?? null,
    [articles, slug]
  );

  const contentHtml = useMemo(() => {
    if (!article?.content) return "";
    try {
      return (lexicalToHtml as (content: unknown) => string)(article.content);
    } catch {
      return "";
    }
  }, [article]);

  if (isLoading) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8">
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
        </BaseCard>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="danger"
          title="No pudimos cargar el artículo"
        >
          <p>{error?.message ?? "Inténtalo nuevamente."}</p>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
          >
            Reintentar
          </BaseButton>
        </BaseAlert>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="warning"
          title="Artículo no encontrado"
        />
        {onBack ? (
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={onBack}
          >
            Volver
          </BaseButton>
        ) : null}
      </section>
    );
  }

  return (
    <article className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8">
      {onBack ? (
        <div>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={onBack}
          >
            ← Volver
          </BaseButton>
        </div>
      ) : null}
      {article.image ? (
        <img
          src={article.image}
          alt={article.title}
          className="h-64 w-full rounded-xl object-cover"
          loading="lazy"
        />
      ) : null}
      <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">{article.title}</h1>
      {article.createdAt ? (
        <p className="text-[var(--font-body)] text-[var(--color-text-tertiary)]">
          Publicado el {formatDate(article.createdAt)}
        </p>
      ) : null}
      {contentHtml ? (
        <div
          className="prose prose-invert max-w-none text-[var(--font-body-lg)] leading-7 text-[var(--color-text)]"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{__html: contentHtml}}
        />
      ) : article.excerpt ? (
        <p className="text-[var(--font-body-lg)] leading-7 text-[var(--color-text)]">{article.excerpt}</p>
      ) : null}
    </article>
  );
};

export default ArticleDetailPage;
